import React from 'react';
import { ArrowRight, Sparkles, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { heroContent } from '../../data/mock';
import { usePreApproval } from '../../pages/LandingPage';

const Hero = () => {
  const { openPreApproval } = usePreApproval();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-950">
      {/* Background Elements */}
      <div className="absolute inset-0">
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/95 to-slate-900" />
        
        {/* Animated Grid */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(16, 185, 129, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(16, 185, 129, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }} />
        </div>

        {/* Glowing Orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* AI Horse-Powered Image - Right Side, Original Direction (Desktop) */}
        <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-[550px] h-[700px]">
          {/* Glow effect behind horse */}
          <div className="absolute inset-0 bg-gradient-to-l from-cyan-500/30 via-emerald-500/15 to-transparent blur-2xl" />
          
          {/* Horse image with fade effect - brighter, fades left and bottom */}
          <div 
            className="relative w-full h-full"
            style={{
              maskImage: 'linear-gradient(to left, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0) 100%), linear-gradient(to top, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 12%, rgba(0,0,0,1) 100%)',
              WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0) 100%), linear-gradient(to top, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 12%, rgba(0,0,0,1) 100%)',
              maskComposite: 'intersect',
              WebkitMaskComposite: 'destination-in'
            }}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_oneforce-wizard/artifacts/sps7nyqh_Untitled%20design%20%2838%29.gif"
              alt="AI Horse-Powered"
              loading="eager"
              fetchpriority="high"
              className="w-full h-full object-contain object-right"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 lg:pt-24 pb-8 lg:pb-16 text-center lg:text-left lg:mr-auto lg:ml-16 xl:ml-32">
        {/* Badge */}
        <div className="inline-flex items-center space-x-2 bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-full px-4 py-2 mb-6">
          <Sparkles className="w-4 h-4 text-emerald-400" />
          <span className="text-sm text-slate-300">AI Horse-Powered Technology</span>
        </div>

        {/* Tagline */}
        <p className="text-base sm:text-lg lg:text-xl text-emerald-400 font-semibold mb-4">
          {heroContent.tagline}
        </p>

        {/* Headline */}
        <h1 className="text-4xl sm:text-5xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
          {heroContent.headline.split('™').map((part, i) => (
            <React.Fragment key={i}>
              {part}
              {i === 0 && <span className="text-emerald-400">™</span>}
            </React.Fragment>
          ))}
        </h1>

        {/* Description */}
        <p className="text-sm sm:text-base lg:text-lg text-slate-300 max-w-3xl mb-5 leading-relaxed lg:text-left text-center">
          {heroContent.description}
        </p>

        {/* Bullet Points */}
        <div className="mb-5">
          <p className="text-slate-400 mb-2 text-sm lg:text-base">{heroContent.bulletIntro}</p>
          <ul className="space-y-1.5 inline-block text-left">
            {heroContent.bullets.map((bullet, index) => (
              <li key={index} className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 lg:w-5 lg:h-5 text-green-400 flex-shrink-0" />
                <span className="text-white text-sm lg:text-base">{bullet}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom Text - No pressure */}
        <p className="text-emerald-400/80 font-medium mb-3 text-sm lg:text-base">
          {heroContent.bottomText}
        </p>

        {/* CTA Text */}
        <p className="text-slate-400 text-xs lg:text-sm mb-6 max-w-2xl">
          {heroContent.ctaText}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4">
          <Button
            onClick={openPreApproval}
            size="lg"
            data-testid="hero-get-qualified-btn"
            className="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-8 py-6 text-lg rounded-xl transition-all hover:shadow-xl hover:shadow-emerald-500/25 hover:-translate-y-0.5 group"
          >
            {heroContent.primaryCta}
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          <a href="#steps">
            <Button
              size="lg"
              variant="outline"
              className="border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white px-8 py-6 text-lg rounded-xl transition-all hover:bg-slate-800/50"
            >
              {heroContent.secondaryCta}
            </Button>
          </a>
        </div>

        {/* Trust Badges */}
        <div className="mt-8 lg:mt-12 flex flex-col items-center lg:items-start">
          <p className="text-slate-500 text-xs lg:text-sm mb-4">Trusted by Best of Best businesses nationwide</p>
          
          {/* Mobile Horse Image - Centered, Big, Bright - Overlapping with Badges */}
          <div className="lg:hidden w-full flex justify-center -mb-12 relative z-0">
            <div className="w-64 h-64 relative">
              {/* Glow effect behind horse */}
              <div className="absolute inset-0 bg-gradient-to-t from-cyan-500/30 via-emerald-500/20 to-transparent blur-2xl rounded-full" />
              <div
                style={{
                  maskImage: 'linear-gradient(to top, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 20%, rgba(0,0,0,1) 100%)',
                  WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 20%, rgba(0,0,0,1) 100%)'
                }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_oneforce-wizard/artifacts/sps7nyqh_Untitled%20design%20%2838%29.gif"
                  alt="AI Horse-Powered"
                  loading="lazy"
                  className="relative z-10 w-full h-full object-contain drop-shadow-2xl"
                />
              </div>
            </div>
          </div>

          {/* Badges */}
          <div className="relative z-10 flex flex-wrap items-center justify-center lg:justify-start gap-4">
            {/* 500+ Businesses Funded Badge */}
            <div className="flex items-center bg-slate-800/80 backdrop-blur-sm border border-emerald-500/40 rounded-full px-5 py-3 animate-pulse" style={{ animationDuration: '3s' }}>
              <div className="text-center">
                <p className="text-emerald-400 font-bold text-sm">500+</p>
                <p className="text-slate-300 text-xs">Businesses Funded</p>
              </div>
            </div>

            {/* Same Day Approval Badge */}
            <div className="flex items-center bg-slate-800/80 backdrop-blur-sm border border-green-500/40 rounded-full px-5 py-3 animate-pulse" style={{ animationDuration: '3.5s' }}>
              <div className="text-center">
                <p className="text-green-400 font-bold text-sm">Same Day</p>
                <p className="text-slate-300 text-xs">Approval Available</p>
              </div>
            </div>

            {/* AI Data Secured Badge */}
            <div className="flex items-center bg-slate-800/80 backdrop-blur-sm border border-cyan-500/40 rounded-full px-5 py-3 animate-pulse" style={{ animationDuration: '4s' }}>
              <div className="text-center">
                <p className="text-cyan-400 font-bold text-sm">AI Data</p>
                <p className="text-slate-300 text-xs">Secured & Protected</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
